import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Col, Container, Row, Alert } from 'react-bootstrap';

// App store URLs
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.GalaxyGames.ARPyramid&hl=en';
const APP_STORE_URL =
  'https://apps.apple.com/eg/app/ar-pyramid-puzzle/id6476737480';

// Timeout to detect if app opened (in milliseconds)
const APP_OPEN_TIMEOUT = 2500;

export const ArPyramidPage = () => {
  const { gameCode } = useParams<{ gameCode: string }>();
  const [showNotInstalled, setShowNotInstalled] = useState(false);

  // Listen for visibility change - if page becomes hidden, app likely opened
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // App opened successfully, clear any pending timeout
      setShowNotInstalled(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  const handleStartGame = () => {
    // Reset notice state
    setShowNotInstalled(false);

    // Custom scheme format: arpyramid://catalystglobal?{baseUrl}?{gameCode}
    const baseUrl =
      window?._env_?.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      '';
    const customSchemeUrl = `arpyramid://catalystglobal?${baseUrl}?${gameCode}`;

    // Set a timeout - if page is still visible after timeout, app didn't open
    const timeoutId = setTimeout(() => {
      if (!document.hidden) {
        setShowNotInstalled(true);
      }
    }, APP_OPEN_TIMEOUT);

    // Try to open the app
    window.location.href = customSchemeUrl;

    // Clean up timeout if page becomes hidden (app opened)
    const cleanup = () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', cleanup);
    };
    document.addEventListener('visibilitychange', cleanup);
  };

  return (
    <div className={'layout layout--ar-pyramid'}>
      <div className={'layout__container'}>
        <Container className={'ar-pyramid-card'}>
          {/* Logo Section */}
          <Row className={'justify-content-center mb-4'}>
            <Col xs={12} className={'text-center'}>
              <img
                src={
                  'https://stsharedappstorage.blob.core.windows.net/dev-catalyst-files/Logo/AR-Pyramid-Quest-Logo.png'
                }
                alt={'AR Pyramid Quest'}
                className={'ar-pyramid-logo-img'}
              />
            </Col>
          </Row>

          {/* App Not Installed Notice */}
          {showNotInstalled && (
            <Row className={'justify-content-center mb-3'}>
              <Col xs={12} md={10} lg={8}>
                <Alert
                  variant={'warning'}
                  onClose={() => setShowNotInstalled(false)}
                  dismissible
                  className={'ar-pyramid-notice'}>
                  <strong>AR Pyramid app not installed.</strong>
                  <br />
                  Please download the app using the links below.
                </Alert>
              </Col>
            </Row>
          )}

          {/* Start Button */}
          <Row className={'justify-content-center mb-3'}>
            <Col xs={12} md={10} lg={8}>
              <Button
                className={'ar-pyramid-start-btn w-100'}
                themeColor={'primary'}
                onClick={handleStartGame}>
                Start the game
              </Button>
            </Col>
          </Row>

          {/* Instructions */}
          <Row className={'justify-content-center mb-4'}>
            <Col xs={12} className={'text-center'}>
              <p className={'ar-pyramid-instructions'}>
                Tap Start the Game to open the AR Pyramid Quest app
                <br />
                and start the game on your device.
              </p>
            </Col>
          </Row>

          {/* Download Section */}
          <Row className={'justify-content-center mb-3'}>
            <Col xs={12} className={'text-center'}>
              <p className={'ar-pyramid-download-text'}>
                <strong>Don't have the app?</strong>
              </p>
            </Col>
          </Row>

          <Row className={'justify-content-center mb-4'}>
            <Col xs={12} className={'text-center'}>
              <div className={'store-badges-wrapper'}>
                <a
                  href={PLAY_STORE_URL}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                  className={'store-badge store-badge--google'}>
                  <img
                    src={
                      'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'
                    }
                    alt={'Get it on Google Play'}
                  />
                </a>
                <a
                  href={APP_STORE_URL}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                  className={'store-badge store-badge--apple'}>
                  <img
                    src={
                      'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg'
                    }
                    alt={'Download on the App Store'}
                  />
                </a>
              </div>
            </Col>
          </Row>

          {/* Tip */}
          <Row className={'justify-content-center'}>
            <Col xs={12} className={'text-center'}>
              <p className={'ar-pyramid-tip'}>
                <strong>Tip:</strong> If the app doesn't open automatically,
                make sure it's installed and up to date, then tap Start the Game
                again.
              </p>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default ArPyramidPage;
